<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

const LOCK_INTERVAL_MS = 50 * 60 * 1000; // 50 minuten
const CLICK_FALLBACK_REQUIRED = 10; // 10 clicks
const CLICK_FALLBACK_RESET_MS = 2000; // 2 seconds
const DEFAULT_SPIN_MS = 8000; // 8 seconds
const DEBUG_AUDIO = false;
const BLOW_INTENSITY_THRESHOLD = 0.45; // 45% intensity
const BLOW_REQUIRED_SECONDS = 1.5; // 1.5 seconds

const now = new Date();
const currentYear = now.getFullYear();
const isAprilFirst = now.getMonth() === 3 && now.getDate() === 1 && (now.getHours() > 8 || (now.getHours() === 8 && now.getMinutes() >= 29));
const enabledStorageKey = `april-fools-windmill-enabled-${currentYear}`;

const isEnabled = ref(true);
const isLocked = ref(false);
const micState = ref<"idle" | "granted" | "denied">("idle");
const spinDurationMs = ref(DEFAULT_SPIN_MS);
const clickCount = ref(0);

let lockTimeout: ReturnType<typeof setTimeout> | null = null;
let clickResetTimeout: ReturnType<typeof setTimeout> | null = null;

let audioContext: AudioContext | null = null;
let analyser: AnalyserNode | null = null;
let sourceNode: MediaStreamAudioSourceNode | null = null;
let mediaStream: MediaStream | null = null;
let animationFrameId: number | null = null;
let blowScoreSeconds = 0;
let lastAudioDebugAt = 0;

function clearLockTimeout() {
    if (lockTimeout !== null) {
        clearTimeout(lockTimeout);
        lockTimeout = null;
    }
}

async function requestMicPermission() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        micState.value = "denied";
        if (DEBUG_AUDIO) {
            console.debug("[WindmillPrank] mediaDevices unavailable");
        }
        return;
    }

    try {
        const permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micState.value = "granted";
        if (DEBUG_AUDIO) {
            console.debug("[WindmillPrank] microphone permission granted");
        }
        permissionStream.getTracks().forEach((track) => track.stop());

        if (isLocked.value) {
            void startMicListening();
        }
    } catch {
        micState.value = "denied";
        if (DEBUG_AUDIO) {
            console.debug("[WindmillPrank] microphone permission denied");
        }
    }
}

function stopMicListening() {
    if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
    }

    if (sourceNode !== null) {
        sourceNode.disconnect();
        sourceNode = null;
    }

    if (analyser !== null) {
        analyser.disconnect();
        analyser = null;
    }

    if (mediaStream !== null) {
        mediaStream.getTracks().forEach((track) => track.stop());
        mediaStream = null;
    }

    if (audioContext !== null) {
        void audioContext.close();
        audioContext = null;
    }
}

function scheduleNextLock() {
    clearLockTimeout();

    if (!isAprilFirst || !isEnabled.value) {
        return;
    }

    lockTimeout = setTimeout(() => {
        isLocked.value = true;
        clickCount.value = 0;
        blowScoreSeconds = 0;
        micState.value = "idle";
        void requestMicPermission();
    }, LOCK_INTERVAL_MS);
}

function unlockScreen() {
    isLocked.value = false;
    clickCount.value = 0;
    blowScoreSeconds = 0;
    spinDurationMs.value = DEFAULT_SPIN_MS;
    stopMicListening();
    scheduleNextLock();
}

async function startMicListening() {
    if (!isLocked.value) {
        return;
    }

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        micState.value = "denied";
        return;
    }

    try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micState.value = "granted";

        audioContext = new AudioContext();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 2048;
        sourceNode = audioContext.createMediaStreamSource(mediaStream);
        sourceNode.connect(analyser);

        const samples = new Uint8Array(analyser.fftSize);
        let previousFrame = performance.now();

        const detectBlow = () => {
            if (!isLocked.value || analyser === null) {
                return;
            }

            const nowFrame = performance.now();
            const deltaSeconds = (nowFrame - previousFrame) / 1000;
            previousFrame = nowFrame;

            analyser.getByteTimeDomainData(samples);

            let sumSquares = 0;
            for (let i = 0; i < samples.length; i += 1) {
                const sample = samples[i] ?? 128;
                const normalized = (sample - 128) / 128;
                sumSquares += normalized * normalized;
            }

            const rms = Math.sqrt(sumSquares / samples.length);
            const intensity = Math.min(1, rms / 0.18);

            if (DEBUG_AUDIO && nowFrame - lastAudioDebugAt > 500) {
                lastAudioDebugAt = nowFrame;
                console.debug("[WindmillPrank] audio", {
                    rms: Number(rms.toFixed(4)),
                    intensity: Number(intensity.toFixed(4)),
                    blowScoreSeconds: Number(blowScoreSeconds.toFixed(3)),
                });
            }

            if (intensity > BLOW_INTENSITY_THRESHOLD) {
                blowScoreSeconds += deltaSeconds;
            } else {
                blowScoreSeconds = Math.max(0, blowScoreSeconds - deltaSeconds * 0.35);
            }

            if (intensity > 0.05) {
                spinDurationMs.value = Math.max(300, Math.round(1800 - intensity * 1400));
            } else {
                spinDurationMs.value = DEFAULT_SPIN_MS;
            }

            if (blowScoreSeconds >= BLOW_REQUIRED_SECONDS || intensity > 0.85) {
                unlockScreen();
                return;
            }

            animationFrameId = requestAnimationFrame(detectBlow);
        };

        animationFrameId = requestAnimationFrame(detectBlow);
    } catch {
        micState.value = "denied";
    }
}

function toggleEnabled() {
    isEnabled.value = !isEnabled.value;
    localStorage.setItem(enabledStorageKey, isEnabled.value ? "1" : "0");

    if (!isEnabled.value) {
        isLocked.value = false;
        clickCount.value = 0;
        blowScoreSeconds = 0;
        spinDurationMs.value = DEFAULT_SPIN_MS;
        stopMicListening();
        clearLockTimeout();
        return;
    }

    scheduleNextLock();
}

function handleWindmillClick() {
    if (!isLocked.value) {
        if (micState.value !== "granted") {
            void requestMicPermission();
        }
        return;
    }

    clickCount.value += 1;

    if (clickResetTimeout !== null) {
        clearTimeout(clickResetTimeout);
    }

    clickResetTimeout = setTimeout(() => {
        clickCount.value = 0;
    }, CLICK_FALLBACK_RESET_MS);

    if (clickCount.value >= CLICK_FALLBACK_REQUIRED) {
        unlockScreen();
    }
}

onMounted(() => {
    if (!isAprilFirst) {
        return;
    }

    const storedEnabled = localStorage.getItem(enabledStorageKey);
    if (storedEnabled === "0") {
        isEnabled.value = false;
        return;
    }

    scheduleNextLock();
});

onBeforeUnmount(() => {
    clearLockTimeout();

    if (clickResetTimeout !== null) {
        clearTimeout(clickResetTimeout);
        clickResetTimeout = null;
    }

    stopMicListening();
});
</script>

<template>
    <div
        v-if="isAprilFirst"
        class="april-fools-root"
    >
        <div class="windmill-widget">
            <button
                class="toggle-btn"
                type="button"
                @click="toggleEnabled"
            >
                {{ isEnabled ? "Windenergie uit" : "Windenergie aan" }}
            </button>

            <button
                class="windmill-btn"
                :disabled="!isEnabled"
                type="button"
                :title="micState === 'denied' ? 'Klik snel om te ontgrendelen' : 'Blaas in je microfoon'"
                @click="handleWindmillClick"
            >
                <svg
                    viewBox="0 0 120 120"
                    class="windmill-svg"
                >
                    <rect
                        x="53"
                        y="46"
                        width="14"
                        height="52"
                        rx="4"
                        class="mill-post"
                    />
                    <g
                        class="mill-blades"
                        :style="{
                            animationDuration: `${spinDurationMs}ms`,
                            animationPlayState: isEnabled && !isLocked ? 'running' : 'paused',
                        }"
                    >
                        <defs>
                            <g id="windmillBladeShape">
                                <path
                                    class="mill-blade-shape"
                                    d="M2507 12295 c-25 -18 -44 -24 -58 -20 -16 5 -63 -21 -195 -109 -96 -64 -174 -122 -174 -128 0 -7 26 -50 57 -96 37 -53 53 -86 46 -90 -6 -4 -200 -133 -432 -287 -272 -181 -421 -286 -421 -297 0 -9 253 -373 562 -810 2201 -3108 2630 -3714 2691 -3801 38 -54 75 -101 82 -103 7 -3 207 123 445 280 237 157 434 286 438 285 6 0 543 -757 565 -795 6 -10 -6 -29 -42 -64 l-51 -50 -138 0 c-126 0 -138 2 -150 20 -7 11 -23 20 -37 20 -14 0 -30 -9 -37 -20 -11 -16 -24 -20 -74 -20 -55 0 -64 3 -78 25 -19 29 -47 32 -70 9 -28 -27 -14 -194 28 -335 9 -29 15 -53 13 -55 -1 -1 -506 -337 -1122 -746 -3272 -2175 -4355 -2898 -4355 -2908 0 -14 238 -350 248 -350 4 0 22 10 41 21 l35 21 26 -33 c17 -19 33 -30 42 -27 8 3 63 38 122 77 59 39 111 71 114 71 4 0 156 -211 337 -468 182 -257 338 -468 347 -470 9 -2 857 556 1884 1240 2785 1852 2979 1982 2981 1998 2 8 -146 224 -327 480 -182 256 -330 468 -330 471 0 3 57 44 126 90 88 58 129 80 138 73 7 -5 44 -32 82 -61 90 -68 238 -144 343 -177 148 -47 241 -57 519 -59 l258 -2 916 -1295 c505 -712 1315 -1857 1802 -2545 487 -687 890 -1251 896 -1253 6 -2 29 8 51 23 25 16 47 24 59 20 23 -7 370 216 370 237 0 7 -36 65 -81 127 -60 85 -77 116 -67 122 97 57 963 647 963 656 0 7 -749 1070 -1665 2363 -1399 1976 -1667 2351 -1685 2348 -11 -3 -231 -145 -488 -317 -258 -172 -474 -314 -481 -317 -7 -3 -25 13 -40 36 -14 22 -22 40 -18 40 15 0 150 139 203 208 72 96 155 268 185 387 30 120 43 277 24 300 -19 23 -57 18 -76 -10 -14 -22 -23 -25 -75 -25 -53 0 -59 2 -71 28 -18 37 -52 40 -81 8 -16 -17 -35 -26 -54 -26 -26 0 -31 5 -42 40 -13 43 -34 60 -76 60 -38 0 -60 -15 -75 -50 l-12 -30 -176 0 c-96 0 -173 3 -170 6 3 3 1301 867 2884 1920 1584 1053 2883 1920 2888 1928 7 10 -24 60 -109 181 -64 91 -123 169 -131 172 -7 3 -27 -5 -45 -18 l-31 -22 -25 32 c-29 36 -44 38 -83 11 l-29 -20 -119 167 c-65 93 -200 283 -300 424 -100 141 -188 262 -196 268 -11 9 -498 -311 -2442 -1605 -1336 -888 -2430 -1622 -2432 -1630 -2 -7 132 -204 297 -438 166 -233 301 -428 301 -433 0 -4 -188 -133 -418 -285 -329 -219 -419 -275 -429 -265 -6 6 -917 1290 -2022 2852 -1106 1562 -2017 2841 -2023 2843 -7 1 -30 -9 -51 -23z"
                                />
                            </g>
                        </defs>
                        <g transform="translate(60 44)">
                            <g transform="scale(0.0056) translate(-6400 -6160)">
                                <use href="#windmillBladeShape" />
                            </g>

                        </g>
                        <circle
                            cx="60"
                            cy="44"
                            r="6"
                            class="mill-core"
                        />
                    </g>
                </svg>
            </button>
        </div>

        <div
            v-if="isEnabled && isLocked"
            class="lock-overlay"
        >
            <button
                class="close-btn"
                type="button"
                @click="unlockScreen"
            >
                Ik puf niet voor BOSS 🙄
            </button>
            <div class="lock-content">
                <h2>🌬️ BOSS heeft jouw groene energie nodig!</h2>
                <p v-if="micState === 'granted'">
                    Blaas flink in je microfoon — één goede ademstoot is genoeg om BOSS weer soepel
                    draaiende te houden voor de volgende 50 minuten. 💚
                </p>
                <p v-else-if="micState === 'idle'">
                    Klik hieronder om je microfoon te activeren en bij te dragen aan een duurzamer 333travel.
                </p>
                <p v-else>
                    Microfoon niet beschikbaar. Wek noodstroom op door {{ CLICK_FALLBACK_REQUIRED }}x
                    op de molen te klikken ({{ clickCount }}/{{ CLICK_FALLBACK_REQUIRED }}).
                </p>
                <p class="click-tip">
                    Tip: je kunt ook altijd {{ CLICK_FALLBACK_REQUIRED }}x op de molen klikken om voldoende noodstroom op te wekken.
                </p>
                <button
                    v-if="micState !== 'granted'"
                    class="permission-btn"
                    type="button"
                    @click="requestMicPermission"
                >
                    Microfoon toestaan
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.april-fools-root {
    position: fixed;
    left: 0;
    bottom: 0;
    z-index: 3100;
    pointer-events: none;
}

.windmill-widget {
    pointer-events: all;
    position: fixed;
    left: 14px;
    bottom: 14px;
    z-index: 3101;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
}

.toggle-btn {
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(13, 13, 13, 0.85);
    color: #fff;
    border-radius: 999px;
    padding: 6px 10px;
    font-size: 12px;
    cursor: pointer;
}

.windmill-btn {
    width: 92px;
    height: 92px;
    border: 0;
    border-radius: 50%;
    background: rgba(20, 20, 20, 0.75);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.35);
    cursor: pointer;
    padding: 0;
}

.windmill-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.windmill-svg {
    width: 92px;
    height: 92px;
}

.mill-post {
    fill: #a79f96;
}

.mill-hub-base {
    fill: #b6aea4;
}

.mill-neck {
    fill: #989086;
}

.mill-core {
    fill: #fff;
    stroke: #d2d2d2;
    stroke-width: 1.5;
}

.mill-blades {
    transform-origin: 60px 44px;
    animation-name: spin;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
}

.mill-blade-shape {
    fill: #f6f6f6;
    stroke: #d4d4d4;
    stroke-width: 80;
}

.lock-overlay {
    position: fixed;
    inset: 0;
    background: rgba(20, 20, 20, 0.75);
    z-index: 3099;
    display: grid;
    place-items: center;
    pointer-events: all;
    isolation: isolate;
}

.lock-content {
    color: #fff;
    text-align: center;
    max-width: 560px;
    padding: 24px;
}

.lock-content h2 {
    margin: 0 0 10px;
    font-size: 32px;
}

.lock-content p {
    margin: 0;
    font-size: 18px;
    opacity: 0.95;
}

.click-tip {
    margin-top: 10px !important;
    font-size: 14px !important;
    opacity: 0.85 !important;
}

.close-btn {
    position: absolute;
    top: 20px;
    right: 20px;
    border: 1px solid rgba(255, 255, 255, 0.45);
    color: #fff;
    background: rgba(255, 255, 255, 0.13);
    border-radius: 8px;
    padding: 9px 16px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
}

.close-btn:hover {
    background: rgba(255, 255, 255, 0.22);
    border-color: rgba(255, 255, 255, 0.65);
}

.permission-btn {
    margin-top: 16px;
    border: 1px solid rgba(255, 255, 255, 0.35);
    color: #fff;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 8px 14px;
    cursor: pointer;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>
